import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useTourGuide, TourGuideConfig, TourStep } from "./useTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "./useTourGuideHover";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { ROUTE_NAMES } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";

export const useMultiDocumentTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const {
    setHighlightedItemKey,
    setActiveTourModule,
    setCurrentTourStep,
    hoverTooltipsEnabled,
  } = useTourGuideContext();
  const { suggestedQuestionsData, faqsData } = useMainChatContext();

  // Tour guide refs
  const textareaRef = useRef<HTMLDivElement>(null);
  const suggestedQuestionsRef = useRef<HTMLDivElement>(null);

  const isMultiDoc = pathname === ROUTE_NAMES.CHECKLIST || pathname.includes("/checklist-chat");

  // Check if step 3 should be included (if suggested questions or FAQs exist)
  const hasSuggestedQuestionsOrFaqs =
    (suggestedQuestionsData?.rows && suggestedQuestionsData?.rows?.length > 0) ||
    (faqsData?.rows && faqsData?.rows?.length > 0);

  // Tour guide configuration for multi-document analysis chatbot
  const multiDocumentTourConfig: TourGuideConfig = useMemo(() => {
    if (!isMultiDoc) {
      return {
        moduleId: "multiDocument",
        steps: [],
        enabled: false,
      };
    }

    const steps: TourStep[] = [
      {
        id: "multi-document-textarea",
        targetRef: textareaRef,
        title:
          t("tourGuide.multiDocument.step1.title") || "Ask Across Multiple Documents",
        description:
          t("tourGuide.multiDocument.step1.description") ||
          "Select multiple documents and ask questions to analyze and compare information across your files. Get insights from all your documents at once.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
      {
        id: "multi-document-chatbot-dropdown",
        targetSelector: '[data-tour-item="project-chatbot-dropdown"]',
        title:
          t("tourGuide.multiDocument.step2.title") ||
          "Upload Files or Switch Modes",
        description:
          t("tourGuide.multiDocument.step2.description") ||
          "Click the plus icon to upload multiple PDF files or switch between different chat modes. You can analyze multiple documents simultaneously.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      },
    ];

    // Add step 3 (suggested questions) only if data exists
    if (hasSuggestedQuestionsOrFaqs) {
      steps.push({
        id: "multi-document-suggested-questions",
        targetRef: suggestedQuestionsRef,
        title:
          t("tourGuide.multiDocument.step3.title") || "Suggested Questions",
        description:
          t("tourGuide.multiDocument.step3.description") ||
          "Browse suggested questions and FAQs to get started quickly. Click on any question to use it as your query.",
        position: "top" as const,
        offset: { x: 0, y: -12 },
      });
    }

    return {
      moduleId: "multiDocument",
      enabled: true,
      steps,
    };
  }, [isMultiDoc, t, hasSuggestedQuestionsOrFaqs]);

  const baseTourGuide = useTourGuide(multiDocumentTourConfig);
  
  // Auto-start tour guide and handle completion tracking
  const multiDocumentTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourCompletionFlag: "multi_document_tour_completed",
    isOnTourPath: isMultiDoc,
  });

  // Sync tour guide with context
  useEffect(() => {
    if (multiDocumentTourGuide.isActive) {
      setActiveTourModule("multiDocument");
      setCurrentTourStep(multiDocumentTourGuide.currentStep);
      setHighlightedItemKey(null);
    } else {
      setActiveTourModule(null);
      setCurrentTourStep(0);
      setHighlightedItemKey(null);
    }
  }, [
    multiDocumentTourGuide.isActive,
    multiDocumentTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
    setHighlightedItemKey,
  ]);

  // Hover tooltips for multi-document tour guide
  const textareaStep = multiDocumentTourConfig.steps.find(
    (step) => step.id === "multi-document-textarea"
  );
  const chatbotDropdownStep = multiDocumentTourConfig.steps.find(
    (step) => step.id === "multi-document-chatbot-dropdown"
  );
  const suggestedQuestionsStep = multiDocumentTourConfig.steps.find(
    (step) => step.id === "multi-document-suggested-questions"
  );

  const textareaHover = useTourGuideHover(
    "multi-document-textarea",
    textareaStep || null
  );
  const chatbotDropdownHover = useTourGuideHover(
    "multi-document-chatbot-dropdown",
    chatbotDropdownStep || null
  );
  const suggestedQuestionsHover = useTourGuideHover(
    "multi-document-suggested-questions",
    suggestedQuestionsStep || null
  );

  // Attach hover handlers to chatbot dropdown via selector
  useEffect(() => {
    if (
      !hoverTooltipsEnabled ||
      multiDocumentTourGuide.isActive ||
      !chatbotDropdownStep
    )
      return;

    const element = document.querySelector(
      '[data-tour-item="project-chatbot-dropdown"]'
    ) as HTMLElement;
    if (element) {
      const handleMouseEnter = () => {
        if (!chatbotDropdownHover.tooltipData) return;
        chatbotDropdownHover.hoverProps.onMouseEnter();
      };
      const handleMouseLeave = () => {
        chatbotDropdownHover.hoverProps.onMouseLeave();
      };

      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [
    hoverTooltipsEnabled,
    multiDocumentTourGuide.isActive,
    chatbotDropdownStep,
    chatbotDropdownHover.hoverProps,
    chatbotDropdownHover.tooltipData,
  ]);

  return {
    textareaRef,
    suggestedQuestionsRef,
    multiDocumentTourGuide,
    multiDocumentTourConfig,
    isMultiDoc,
    textareaHover,
    chatbotDropdownHover,
    suggestedQuestionsHover,
  };
};

